<template>
  <div v-if="$apollo.queries.settings.loading && $apollo.queries.posts.loading">
    <div class="w-full h-[400px] bg-gray-lightest animate-pulse" role="status">
      <span class="sr-only">Loading...</span>
    </div>

    <div class="container mb-16">
      <ParagraphSkeleton class="my-10" />

      <div class="flex gap-6 lg:gap-16">
        <PostCardSkeleton
          v-for="n in 3"
          :key="n"
          :class="{ 'md:col-span-2': n === 1 }"
        />
      </div>

      <div class="bg-gray-lightest h-[35px] rounded w-65"></div>

      <ParagraphSkeleton class="my-10" />
    </div>

    <span class="sr-only">Loading...</span>
  </div>
  <div v-else>
    <VisualHero
      preload
      :image="visualBackgroundImage"
      background-color="bg-orange-news"
    >
      <template slot="title">{{ pageTitle }}</template>
      <p slot="description" v-dompurify-html="homepageText" />
    </VisualHero>
    <section class="pt-10">
      <div class="container mx-auto">
        <Swiper :options="swiperOptions">
          <Card
            v-for="(cat, key) in homeCategories"
            :key="key"
            :image="{
              src: $utilities.imgAlias(cat.immagine.mediaItemUrl),
              alt: cat.immagine.altText ?? cat.immagine.title ?? null,
            }"
            :caption="cat.titolo"
            caption-size="small"
            :show-gradient="true"
            type="vertical"
            class="swiper-slide"
          >
            <template #content>
              <div v-dompurify-html="cat.testo" class="pt-4 text-lg"></div>
            </template>
            <template #footer>
              <NuxtLink
                :to="`/news/${cat.categoria.slug}/`"
                class="flex items-center pt-4 font-bold uppercase text-navitem text-blue"
              >
                Scopri la categoria
                <SvgIcon name="angle-right-blue" class="ml-4 max-h-5 max-w-5" />
              </NuxtLink>
            </template>
          </Card>
        </Swiper>
      </div>
    </section>

    <section class="py-10">
      <div class="container">
        <h2 class="font-bold text-section-title">
          {{ blockArticlesTitle }}
        </h2>
        <div v-dompurify-html="blockArticlesSubtitle" class="py-8"></div>

        <div class="space-y-10">
          <PostCard
            v-for="(post, key) in latestPost"
            :key="key"
            :post="post"
            type="horizontal"
          />

          <template v-if="$apollo.queries.posts.loading">
            <PostCardSkeleton
              v-for="n in 3"
              :key="n"
              type="horizontal"
              class="w-full"
            />
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// Components
import Card from '~/components/News/Card.vue'
import ParagraphSkeleton from '~/components/News/ParagraphSkeleton.vue'
import PostCard from '~/components/News/PostCard.vue'
import PostCardSkeleton from '~/components/News/PostCardSkeleton.vue'
import Swiper from '~/components/Swiper.vue'
import VisualHero from '~/components/VisualHero.vue'

// GraphQL
import settings from '~/apollo/queries/settings.gql'
import posts from '~/apollo/queries/posts.gql'

export default {
  components: {
    Card,
    ParagraphSkeleton,
    PostCard,
    PostCardSkeleton,
    Swiper,
    VisualHero,
  },

  apollo: {
    settings: {
      query: settings,
      prefetch: true,
      update(data) {
        return data
      },
    },
    posts: {
      query: posts,
      prefetch: true,
      variables() {
        return {
          first: 4,
        }
      },
      update(data) {
        return data.posts
      },
    },
  },
  data() {
    return {
      swiperOptions: {
        observer: true,
        observeParents: true,
        watchOverflow: true,
        slidesPerView: 1.2,
        spaceBetween: 15,
        grabCursor: true,
        loopAdditionalSlides: 1,
        loop: false,
        breakpoints: {
          1024: {
            slidesPerView: 3,
            spaceBetween: 40,
          },
          480: {
            slidesPerView: 2.2,
          },
        },
      },
    }
  },
  head() {
    return {
      title: this.settings?.seo?.meta?.homepage?.title ?? this.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content:
            this.settings?.seo?.meta?.homepage?.description ??
            this.settings?.generalSettings?.description ??
            '',
        },
      ],
      script: [
        {
          type: 'text/javascript',
          innerHTML: `var utag_data = ${JSON.stringify({
            site_name: this.settings?.generalSettings?.title,
            site_description: this.settings?.generalSettings?.description ?? '',
            page_type: 'homepage',
            user_role: 'guest',
          })}`,
        },
      ],
      __dangerouslyDisableSanitizers: ['innerHTML'],
    }
  },
  computed: {
    options() {
      return this.settings?.acfOptions?.acfOptions
    },
    pageTitle() {
      return this.options?.titoloHomepage
    },

    visualBackgroundImage() {
      const image = this.$utilities.imgAlias(
        this.options?.immagineInTestata.mediaItemUrl
      )

      return image
    },

    homeCategories() {
      return this.options?.categorieInEvidenza
    },
    homepageText() {
      return this.options?.testoHomepage
    },
    blockArticlesTitle() {
      return this.options?.titoloBloccoArticoli
    },
    blockArticlesSubtitle() {
      return this.options?.testoBloccoArticoli
    },
    latestPost() {
      return this.posts?.edges.map(({ node: post }) =>
        this.$utilities.mapPost(post)
      )
    },
    seoTitle() {
      return this.options?.titoloHomepage
    },
  },
  methods: {
    getExcerpt(content) {
      const excerpt = content
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      return `${excerpt.substring(0, 150)}...`
    },
  },
}
</script>
